/**
 * Lazy loader for the @emoji-mart/data dataset.
 *
 * Keeps the ~433 KB (gzip) / ~1.5 MB (raw) emoji dataset in a separate async
 * chunk instead of the main bundle, and centralizes the module-scoped cached
 * promise so every caller shares a single load attempt.
 */

// A static `import type` here would defeat the point: it's the same
// specifier this file dynamically imports to keep the ~1.5MB dataset in its
// own chunk.
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
export type EmojiMartData = import('@emoji-mart/data').EmojiMartData;

let dataPromise: Promise<EmojiMartData> | null = null;

/**
 * Lazily load the full @emoji-mart/data dataset as a separate async chunk.
 * First-call-wins: the promise is built once and shared by all concurrent
 * callers. On failure it warns once, resets the cache (so the next `:` trigger
 * retries), and RETHROWS — every caller MUST attach a rejection handler
 * (e.g. `.catch(() => {})`) or the rejection surfaces as unhandled. The
 * note/conversation sibling issue reuses this loader.
 */
export function loadEmojiData(): Promise<EmojiMartData> {
  if (!dataPromise) {
    dataPromise = import('@emoji-mart/data')
      .then(mod => mod.default as EmojiMartData)
      .catch(error => {
        console.warn(
          '[emoji] Failed to load @emoji-mart/data; emoji autocomplete is unavailable until the next trigger.',
          error
        );
        dataPromise = null;
        throw error;
      });
  }
  return dataPromise;
}
